import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import NotificationToast from "./NotificationToast";

const MAX_TOASTS = 3;
const FRESH_TOAST_MS = 20000;

const SETTINGS_KEYS = {
    toasts: "ao_notify_toasts_enabled",
};

const getStoredBool = (key, fallback = true) => {
    try {
        const value = localStorage.getItem(key);

        if (value === null) return fallback;

        return value !== "false";
    } catch (_error) {
        return fallback;
    }
};

const getNotificationSource = (raw) => {
    return raw?.notification || raw || {};
};

const getToastKey = (raw) => {
    const item = getNotificationSource(raw);
    const id = item?._id || item?.id;

    if (id) return `id:${id}`;

    const meta = item?.meta || {};
    const scope = item?.scope || meta.scope || "personal";
    const type = item?.type || "notification";
    const event = meta.event || type;

    const chatId = meta.chatId || "";
    const messageId = meta.messageId || "";
    const orderId = meta.orderId || "";
    const targetId = meta.targetId || "";
    const paymentId = meta.paymentId || "";
    const officeId = meta.officeId || item.officeId || item.office || "";

    if (event === "chat_message" || type === "chat") {
        return `${scope}:chat:${chatId}:${messageId}:${officeId}`;
    }

    return `${scope}:${event}:${orderId || targetId || paymentId}:${officeId}`;
};

const normalizeToast = (raw) => {
    const data = getNotificationSource(raw);

    if (!data || typeof data !== "object") return null;

    return {
        ...data,
        _toastKey: getToastKey(data),
        scope: data.scope || data.meta?.scope || "personal",
        createdAt: data.createdAt || new Date().toISOString(),
    };
};

const isFreshNotification = (notification) => {
    const createdAt = notification?.createdAt
        ? new Date(notification.createdAt).getTime()
        : Date.now();

    return Date.now() - createdAt <= FRESH_TOAST_MS;
};

const isBillingNotification = (notification = {}) => {
    const type = String(notification?.type || '').toLowerCase();
    const event = String(notification?.meta?.event || '').toLowerCase();
    return type === 'payment' || type === 'billing' || event.startsWith('billing_');
};

const isToastAllowedForContext = (notification, {
    showOfficeTabs = false,
    customerFacingMode = false,
} = {}) => {
    if (customerFacingMode && isBillingNotification(notification)) return false;

    const scope = notification?.scope === "office" ? "office" : "personal";

    if (scope !== "office") return true;

    // In the office workspace we intentionally surface activity from every
    // office the user can access. The payload remains a notification only;
    // chat messages themselves are still scoped by ChatRealtimeBridge rooms.
    return !!showOfficeTabs;
};

const NotificationToastHost = ({
    officeId = null,
    showOfficeTabs = false,
}) => {
    const location = useLocation();
    const [toastList, setToastList] = useState([]);
    const [toastsEnabled, setToastsEnabled] = useState(() => getStoredBool(SETTINGS_KEYS.toasts, true));

    const seenToastKeysRef = useRef(new Set());
    const reduxWatchInitializedRef = useRef(false);

    const notifications = useSelector((state) => state.notification?.notifications || []);
    const cleanOfficeId = officeId && officeId !== "all" ? officeId : null;
    const customerFacingMode = (
        location.pathname.startsWith('/customer-session/') ||
        location.pathname.startsWith('/scene-access/') ||
        new URLSearchParams(location.search || '').get('tab') === 'calc3d'
    );

    useEffect(() => {
        setToastList((prev) => prev.filter((toast) => (
            isToastAllowedForContext(toast, {
                officeId: cleanOfficeId,
                showOfficeTabs,
                customerFacingMode,
            })
        )));
    }, [cleanOfficeId, showOfficeTabs, customerFacingMode]);

    useEffect(() => {
        const handler = () => {
            const enabled = getStoredBool(SETTINGS_KEYS.toasts, true);

            setToastsEnabled(enabled);

            if (!enabled) {
                setToastList([]);
            }
        };

        window.addEventListener("notification-settings:changed", handler);

        return () => {
            window.removeEventListener("notification-settings:changed", handler);
        };
    }, []);

    const pushToast = useCallback((raw) => {
        const toast = normalizeToast(raw);

        if (!toast) return;
        if (!isToastAllowedForContext(toast, {
            officeId: cleanOfficeId,
            showOfficeTabs,
            customerFacingMode,
        })) return;

        const key = toast._toastKey || getToastKey(toast);

        // Active-chat notifications are stored in Redux briefly so unread
        // counters stay consistent until BACK confirms mark-read. They must
        // never become a toast through the Redux watcher.
        if (toast.__suppressToast === true) {
            seenToastKeysRef.current.add(key);
            return;
        }

        if (!getStoredBool(SETTINGS_KEYS.toasts, true)) {
            seenToastKeysRef.current.add(key);
            return;
        }

        if (seenToastKeysRef.current.has(key)) {
            return;
        }

        seenToastKeysRef.current.add(key);

        if (seenToastKeysRef.current.size > 500) {
            const first = seenToastKeysRef.current.values().next().value;
            seenToastKeysRef.current.delete(first);
        }

        setToastList((prev) => {
            if (prev.some((item) => (item._toastKey || getToastKey(item)) === key)) {
                return prev;
            }

            return [...prev, toast].slice(-MAX_TOASTS);
        });
    }, [cleanOfficeId, showOfficeTabs, customerFacingMode]);

    const drainWindowToastQueue = useCallback(() => {
        if (typeof window === "undefined") return;

        const queue = Array.isArray(window.__notificationToastQueue)
            ? window.__notificationToastQueue
            : [];

        if (!queue.length) return;

        queue.forEach((item) => {
            pushToast(item);
        });

        window.__notificationToastQueue = [];
    }, [pushToast]);

    useEffect(() => {
        drainWindowToastQueue();

        const interval = setInterval(() => {
            drainWindowToastQueue();
        }, 500);

        return () => clearInterval(interval);
    }, [drainWindowToastQueue]);

    useEffect(() => {
        const onUserToast = (event) => {
            pushToast(event?.detail);
        };

        const onOfficeToast = (event) => {
            pushToast(event?.detail);
        };

        const onAnyToast = (event) => {
            pushToast(event?.detail);
        };

        const onSceneClipboardNotice = (event) => {
            const detail = event?.detail || {};
            const text = String(detail.text || "").trim();

            if (!text) return;

            const ts = Number(detail.ts) || Date.now();
            const isError = detail.type === "error";

            pushToast({
                _id: `scene-clipboard:${ts}:${Math.random().toString(36).slice(2, 8)}`,
                title: isError ? "3D-сцена · ошибка" : "3D-сцена",
                message: text,
                type: "system",
                scope: "personal",
                createdAt: new Date(ts).toISOString(),
                meta: {
                    event: "scene_clipboard",
                    severity: isError ? "error" : "success",
                    scope: "personal",
                },
            });
        };

        window.addEventListener("user-toast", onUserToast);
        window.addEventListener("office-toast", onOfficeToast);
        window.addEventListener("notification-toast", onAnyToast);
        window.addEventListener("officeSalesSession:sceneClipboardNotice", onSceneClipboardNotice);

        return () => {
            window.removeEventListener("user-toast", onUserToast);
            window.removeEventListener("office-toast", onOfficeToast);
            window.removeEventListener("notification-toast", onAnyToast);
            window.removeEventListener("officeSalesSession:sceneClipboardNotice", onSceneClipboardNotice);
        };
    }, [pushToast]);

    useEffect(() => {
        const list = Array.isArray(notifications) ? notifications : [];

        if (!reduxWatchInitializedRef.current) {
            list.forEach((item) => {
                const key = getToastKey(item);

                if (!isFreshNotification(item)) {
                    seenToastKeysRef.current.add(key);
                }
            });

            reduxWatchInitializedRef.current = true;
        }

        list.slice(0, 12).forEach((item) => {
            if (!item || item.isRead) return;
            if (!isFreshNotification(item)) return;

            pushToast(item);
        });
    }, [notifications, pushToast]);

    const handleCloseToast = useCallback((toastKey) => {
        setToastList((prev) => (
            prev.filter((item) => (item._toastKey || getToastKey(item)) !== toastKey)
        ));
    }, []);

    if (!toastsEnabled || !toastList.length || typeof document === "undefined") {
        return null;
    }

    return createPortal(
        <div className="notification-toast-stack-AO">
            {toastList.filter(Boolean).map((toast) => {
                const key = toast._toastKey || getToastKey(toast);

                return (
                    <NotificationToast
                        key={key}
                        data={toast}
                        onClose={() => handleCloseToast(key)}
                    />
                );
            })}
        </div>,
        document.body
    );
};

export default NotificationToastHost;
